import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, MessageCircle, LogOut, Plus, ShieldCheck, UserCircle2, Sparkles, Loader2, Shield, ExternalLink, Bell, Crown, Package, Ticket, CalendarClock, Heart, Gift, Handshake, History } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { HEADER } from "@/constants/testIds";
import { sharedApi } from "@/lib/api";
import { recordRecentSearch } from "@/lib/searchRecent";
import { toast } from "sonner";
import CategoryNavBar from "@/components/layout/CategoryNavBar";
import { usePendingReviews } from "@/components/engage/usePendingReviews";
import { usePointsBalance } from "@/hooks/usePoints";
import { Star } from "lucide-react";
import ChatUnreadBadge from "@/components/layout/ChatUnreadBadge";
import QuickPillsBar from "@/components/layout/QuickPillsBar";
import NotificationBell from "@/components/layout/NotificationBell";
import TranslationSwitcher from "@/components/detail/TranslationSwitcher";
import TopSearchResults from "@/components/layout/TopSearchResults";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useI18n } from "@/lib/i18n";
import { gaEvent } from "@/lib/ga";
import { resolveLogoUrl } from "@/lib/branding";

export default function Header() {
  const { t } = useI18n(); // Iter 704 — whole-site language
  const { user, logout } = useAuth();
  // Iter-329 — pending-review count bubble on the "My visits" menu item
  // so the 48-hour earn-back window is impossible to miss.
  const pendingQ = usePendingReviews(!!user);
  const pendingCount = pendingQ.data?.count || 0;
  // Iter-291 §1 — server payload includes `admin_sites`. NON-NULL means
  // the user is a linked admin/sub-admin who should see the
  // "Switch to Admin" item INSIDE the profile dropdown (never a top-
  // level tab). An empty array [] still counts as linked (site scope
  // may be widened server-side). Only `null`/`undefined` hides it.
  // Iter 729 — backend now returns `is_admin` + `linked_admin_id` on
  // /auth/me and logins (owner included); keep admin_sites for older
  // cached sessions.
  const hasAdminAccess =
    user != null &&
    (user.is_admin === true || user.linked_admin_id != null || Array.isArray(user.admin_sites));
  const { data: settings = {} } = useSiteSettings();
  const logoUrl = resolveLogoUrl(settings);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // "N saved searches" counter next to the "My alerts" menu item.
  // Uses the same query key as SaveSearchButton so the badge stays in sync
  // when the user saves/removes a search anywhere in the app.
  const savedSearchesQ = useQuery({
    queryKey: ["saved-searches", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/saved-searches");
      return data?.saved_searches || [];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });
  const savedCount = savedSearchesQ.data?.length || 0;

  const submitSearch = async (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    // Iter-282 — record every submitted search so trending + recents feed
    // stays accurate regardless of which surface fired the submit (Header
    // Enter vs. panel chip vs. See all).
    recordRecentSearch(query, Boolean(user?.id), qc);
    gaEvent("search", { search_term: query });
    if (!aiMode) {
      nav(`/search?q=${encodeURIComponent(query)}`);
      return;
    }
    try {
      setAiLoading(true);
      const { data } = await sharedApi.post("/classifieds/ai/smart-search", { query, country: "NZ" });
      const f = data.filters || {};
      const params = new URLSearchParams();
      if (f.q) params.set("q", f.q);
      if (f.category) params.set("category", f.category);
      if (f.region) params.set("region", f.region);
      if (f.city) params.set("city", f.city);
      if (f.min_price != null) params.set("min_price", String(f.min_price));
      if (f.max_price != null) params.set("max_price", String(f.max_price));
      if (f.sort) params.set("sort", f.sort);
      nav(`/search?${params.toString()}`);
    } catch {
      toast.error("Smart search couldn't parse that — using keyword search.");
      nav(`/search?q=${encodeURIComponent(query)}`);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <header data-testid={HEADER.root} className="sticky top-0 z-40 bg-white border-b border-soft">
      {/* Top strip: greeting + logo | search | account.
          Mobile: logo + account cluster share row 1, search wraps to its
          own full-width row 2 (saves ~60px of chrome). Desktop unchanged. */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 md:gap-6">
        {/* Left: greeting + official AllSale™ mark from site-settings */}
        <Link to="/" data-testid={HEADER.logo} className="order-1 inline-flex items-center gap-3 shrink-0">
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[11px] font-black tracking-[0.24em] uppercase" style={{ color: "var(--brand-orange)" }}>
              KIA ORA
            </span>
            <span className="text-[11px] font-semibold text-slate-700 tracking-wider">New Zealand</span>
          </div>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="AllSale"
              data-testid="header-logo-img"
              className="h-9 md:h-10 w-auto shrink-0"
              draggable={false}
            />
          ) : (
            <span
              data-testid="header-logo-fallback"
              className="font-display text-3xl md:text-4xl font-black tracking-tighter leading-none"
              aria-label="AllSale"
            >
              <span style={{ color: "var(--brand-blue)" }}>All</span>
              <span style={{ color: "var(--brand-orange)" }}>Sale</span>
            </span>
          )}
        </Link>

        {/* Center: pill search with smart-search toggle */}
        <form onSubmit={submitSearch} className="relative order-3 md:order-2 basis-full md:basis-auto md:flex-1 md:max-w-xl md:mx-auto w-full md:w-auto">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => {
              // Delay blur so a click inside the panel registers first.
              setTimeout(() => setSearchFocused(false), 120);
            }}
            type="text"
            placeholder={aiMode ? t("Try: silver Toyota under $15k in Auckland…") : t("Search...")}
            className={`w-full pl-11 pr-32 py-2.5 rounded-full border focus:outline-none text-sm placeholder:text-slate-400 transition-colors ${
              aiMode
                ? "bg-white border-[color:var(--brand-blue)] ring-2 ring-[color:var(--brand-blue)]/15"
                : "bg-slate-50 border-soft focus:border-[color:var(--brand-blue)]"
            }`}
            aria-label="Search Allsale"
          />
          <button
            type="button"
            data-testid="header-ai-toggle"
            onClick={() => setAiMode((s) => !s)}
            title={aiMode ? "Turn off smart search" : "Smart search"}
            className={`absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${
              aiMode
                ? "bg-[color:var(--brand-blue)] text-white"
                : "bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue)] hover:text-white"
            }`}
          >
            {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {t("Smart")}
          </button>
          {/* Iter-277 §1 — Whole-site grouped search results below the input */}
          <TopSearchResults
            query={q}
            open={searchFocused && !aiMode}
            onNavigate={() => { setQ(""); setSearchFocused(false); }}
          />
        </form>

        {/* Right: account */}
        <div className="order-2 md:order-3 ml-auto md:ml-0 flex items-center gap-2 justify-end shrink-0">
          {/* Iter-276f global language pill — always visible (mobile + desktop),
              collapses to flag-only under 380px so the row never wraps. */}
          <TranslationSwitcher />
          {user ? (
            <>
              <Link data-testid={HEADER.chatBtn} to="/chat" className="relative p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors" title="Chat">
                <MessageCircle className="w-5 h-5" />
                <ChatUnreadBadge />
              </Link>
              <NotificationBell />
              {/* Iter-337 — NZ-points pill parity with the app header. */}
              <NzPointsPill />
              <Link data-testid={HEADER.myAdsBtn} to="/my-ads" className="hidden sm:block px-3 py-1.5 text-sm font-semibold whitespace-nowrap text-slate-700 hover:text-[color:var(--brand-blue)] transition-colors">
                {t("My ads")}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid={HEADER.userMenu}
                    className="w-8 h-8 rounded-full bg-[color:var(--brand-blue)] text-white grid place-items-center text-sm font-bold hover:brightness-110 transition-[filter]"
                    aria-label="Account menu"
                  >
                    {(user.full_name || user.email || "U").slice(0, 1).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">
                    {user.full_name || user.email || "My account"}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-ads" to="/my-ads" className="cursor-pointer">
                      <Package className="w-4 h-4" /> {t("My ads")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-vouchers" to="/my/vouchers" className="cursor-pointer">
                      <Ticket className="w-4 h-4" /> {t("My vouchers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-visits" to="/my/visits" className="cursor-pointer">
                      <History className="w-4 h-4" /> {t("My visits")}
                      {pendingCount > 0 && (
                        <span
                          data-testid="header-pending-reviews-bubble"
                          className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-black px-1"
                        >
                          {pendingCount}
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-favorites" to="/my/favorites" className="cursor-pointer">
                      <Heart className="w-4 h-4" /> {t("My favourites")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-rewards" to="/rewards" className="cursor-pointer">
                      <Gift className="w-4 h-4" /> {t("Rewards")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-account" to="/account" className="cursor-pointer">
                      <UserCircle2 className="w-4 h-4" /> {t("Account")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-alerts" to="/my/alerts" className="cursor-pointer">
                      <Bell className="w-4 h-4" /> {t("My alerts")}
                      {savedCount > 0 && (
                        <span
                          data-testid="header-saved-searches-badge"
                          className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[color:var(--brand-blue)] text-white text-[10px] font-bold"
                          aria-label={`${savedCount} saved searches`}
                          title={`${savedCount} saved ${savedCount === 1 ? "search" : "searches"}`}
                        >
                          {savedCount > 99 ? "99+" : savedCount}
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-viewings" to="/my/viewing-requests" className="cursor-pointer">
                      <CalendarClock className="w-4 h-4" /> {t("My viewing requests")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-events" to="/my/events" className="cursor-pointer">
                      <CalendarClock className="w-4 h-4" /> {t("My events")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-deals" to="/my/deals" className="cursor-pointer">
                      <Ticket className="w-4 h-4" /> {t("My deals")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-my-subscription" to="/my-subscription" className="cursor-pointer">
                      <Crown className="w-4 h-4" /> {t("My subscription")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link data-testid="header-menu-partner-program" to="/partner-program" className="cursor-pointer">
                      <Handshake className="w-4 h-4" /> {t("Partner Program")}
                    </Link>
                  </DropdownMenuItem>
                  {hasAdminAccess && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        data-testid="header-menu-switch-admin"
                        onSelect={async () => {
                          // Iter-301 — mint a one-time handoff token at
                          // click-time (60s TTL, single-use) and hand off
                          // to the admin panel via the URL the backend
                          // returns. Same-tab navigation so the browser's
                          // Back button returns the admin here.
                          try {
                            const { data } = await sharedApi.post(
                              "/auth/admin-handoff/start",
                              {},
                              { headers: { "Cache-Control": "no-store" } },
                            );
                            if (data?.admin_url) {
                              window.location.assign(data.admin_url);
                              return;
                            }
                            throw new Error("no_admin_url");
                          } catch (err) {
                            const status = err?.response?.status;
                            if (status === 403) {
                              toast.error("Admin access unavailable.");
                            } else {
                              toast.error("Couldn't start your admin session — please try again.");
                            }
                          }
                        }}
                        className="cursor-pointer"
                      >
                        <Shield className="w-4 h-4" /> Switch to Admin
                        <ExternalLink className="w-3 h-3 opacity-70 ml-auto" />
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid={HEADER.logoutBtn}
                    onSelect={() => { nav("/", { replace: true }); setTimeout(() => logout(), 0); }}
                    className="cursor-pointer text-red-600 focus:text-red-700"
                  >
                    <LogOut className="w-4 h-4" /> {t("Sign out")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-1">
              <Link data-testid={HEADER.loginBtn} to="/login" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:text-[color:var(--brand-blue)] transition-colors">
                <UserCircle2 className="w-4 h-4" /> {t("Sign In")}
              </Link>
              <Link data-testid={HEADER.registerBtn} to="/register" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-full text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-wash)] transition-colors">
                <UserCircle2 className="w-4 h-4" /> {t("Sign Up")}
              </Link>
              <span className="hidden lg:inline-flex items-center gap-1 pl-2 ml-1 border-l border-slate-200 text-[10px] text-slate-500 font-semibold uppercase tracking-widest">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {t("Verified · Trusted")}
              </span>
            </div>
          )}
          <Link
            data-testid={HEADER.postAdBtn}
            to="/sell"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold shadow-card transition-colors"
          >
            <Plus className="w-4 h-4" /> {t("Post")}
          </Link>
          {/* Mobile-only Sign In pill */}
          {!user && (
            <Link data-testid={HEADER.loginBtn + "-mobile"} to="/login" className="md:hidden inline-flex items-center px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[color:var(--brand-blue)]">
              {t("Sign In")}
            </Link>
          )}
        </div>
      </div>

      {/* Iter-219 pills bar — admin-managed via /api/classifieds/nav */}
      <QuickPillsBar />

      {/* Category nav bar */}
      <CategoryNavBar />
    </header>
  );
}

/* Iter-337/349 — same NZ-points pill as the app header. Hidden until the
   balance loads (and entirely when the wallet fields aren't served).
   Iter-349 row 32: compact `1.2k` format above 9,999. */
function formatPoints(n) {
  if (n > 9999) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

function NzPointsPill() {
  const q = usePointsBalance();
  const nz = q.data?.nz_classified_balance;
  if (nz == null) return null;
  return (
    <Link
      to="/rewards"
      data-testid="header-nz-points-pill"
      title="Allsale Rewards — NZ stores wallet"
      className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold px-2.5 py-1 hover:bg-amber-100 transition-colors"
    >
      <Star className="w-3.5 h-3.5 text-amber-500" /> {formatPoints(nz)} pts
    </Link>
  );
}
